import { ReactNode, type JSX } from "react";
import { AriaSelectProps } from "react-aria";
import { Select as AriaSelect } from "react-aria-components";

import ListBox from "ds/components/ListBox";
import ListBoxPopover from "ds/components/ListBox/Popover";
import ListBoxScrollableWrapper from "ds/components/ListBox/ScrollableWrapper";
import useEnsureId from "hooks/useEnsureId";

import SelectEmptyCollection from "./EmptyCollection";
import SelectInput, { SelectInputProps } from "./Input";
import Item from "./Item";
import styles from "./styles.module.css";
import { BaseSelectItem } from "./types";

type SelectProps<Collection extends BaseSelectItem, Key extends string> = Omit<
  AriaSelectProps<Collection>,
  // label, helper/error text and validation state are rendered by FormField, not by Select
  | "children"
  | "onSelectionChange"
  | "onChange"
  | "label"
  | "description"
  | "errorMessage"
  | "isInvalid"
> & {
  id?: string;
  isError?: boolean;
  placeholder?: string;
  value: string;
  items: Collection[];
  onChange: (value: Key) => void;
  renderInput?: (input: SelectInputProps) => ReactNode;
  children?: (Collection: Collection) => JSX.Element;
  isDisabled?: boolean;
  isLoading?: boolean;
  size?: SelectInputProps["size"];
  color?: SelectInputProps["color"];
};

const Select = <Collection extends BaseSelectItem, Key extends string>({
  id: propsId,
  children = (item) => <Item {...item} id={item.value} />,
  isError,
  size,
  color,
  value,
  renderInput = (props) => <SelectInput {...props} />,
  onChange,
  items,
  placeholder = "Select an option",
  isDisabled: propsIsDisabled,
  isLoading,
  ...rest
}: SelectProps<Collection, Key>) => {
  const id = useEnsureId(propsId);
  const isDisabled = propsIsDisabled || !items?.length;

  return (
    <AriaSelect
      className={styles.select}
      value={value}
      onChange={(key) => key != null && onChange(key as Key)}
      isInvalid={isError}
      isDisabled={isDisabled}
      placeholder={placeholder}
    >
      {renderInput({
        id,
        isError,
        isDisabled,
        isLoading,
        size,
        color,
        ...rest,
      })}
      <ListBoxPopover>
        <ListBoxScrollableWrapper>
          <ListBox<Collection>
            items={items}
            renderEmptyState={() => <SelectEmptyCollection isLoading={isLoading} />}
          >
            {children}
          </ListBox>
        </ListBoxScrollableWrapper>
      </ListBoxPopover>
    </AriaSelect>
  );
};

Select.displayName = "DS.Select";

export default Select;
